// src/components/Question.jsx
import React, { useEffect, useState } from "react";
import MKHHeader from "../components/header/MKHHeader";
import SJHeader from "../components/header/SJHeader";
import TarbaaHeader from "../components/header/TarbaaHeader";
import TarbaaQuestion from "../components/tarbaa/Question";
import TarbaaAnswer from "../components/tarbaa/Answer";
import MKHQuestion from "../components/mkh/Question";
import MKHAnswer from "../components/mkh/Answer";
import MKHWhoAnswered from "../components/mkh/WhoAnswered";
import SJQuestion from "../components/sj/Question";
import { useParams } from "react-router-dom";
import WhoAnswered from "../components/tarbaa/WhoAnswered";

// Import encryption utilities
import { encryptData, decryptData } from "../funcs/encryption";
import SJAnswer from "../components/sj/Answer";
import SJWhoAnswered from "../components/sj/WhoAnswered";
import * as webGmBridge from "../live/webGmBridge";
import QuestionIntro from "../components/intro/QuestionIntro";
import SharedDrawHost from "../draw/SharedDrawHost";
import useDrawHost from "../draw/useDrawHost";
import { resolveQuestionIntro } from "../funcs/questionIntro";

const Question = () => {
  const { id } = useParams();
  const [theme, setTheme] = useState("Tarbaa");
  const [game, setGame] = useState(null);
  const [ads, setAds] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showWhoAnswered, setShowWhoAnswered] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);

  // Retrieve encrypted data from localStorage and decrypt it
  useEffect(() => {
    const encryptedTheme = localStorage.getItem("theme");
    const encryptedGame = localStorage.getItem("game");
    const encryptedAds = localStorage.getItem("ads");

    if (encryptedTheme) {
      setTheme(encryptedTheme || "Tarbaa");
    }

    if (encryptedGame) {
      const decryptedGame = decryptData(encryptedGame);
      setGame(decryptedGame);
    }

    if (encryptedAds) {
      setAds(encryptedAds);
    }
  }, []);

  // Encrypt and save game data to localStorage whenever it changes
  useEffect(() => {
    if (game !== null) {
      const encryptedGame = encryptData(game);
      localStorage.setItem("game", encryptedGame);
    }
  }, [game]);

  // Optionally, encrypt and save theme if it changes (if your application allows theme changes)
  // Add similar useEffect for theme if needed

  // Determine the current question based on the `id` from params
  const question = game?.modes
    ?.find((mode) => mode.questions.find((q) => q._id === id))
    .questions.find((q) => q._id === id).question
    ? {
        ...game?.modes
          ?.find((mode) => mode.questions.find((q) => q._id === id))
          .questions.find((q) => q._id === id).question,
        custom: false,
      }
    : {
        ...game?.modes
          ?.find((mode) => mode.questions.find((q) => q._id === id))
          .questions.find((q) => q._id === id).customQuestion,
        custom: true,
      };

  // Intro screen (how to play) shown before the question, when the mode or
  // the question has one.
  const questionMode = game?.modes?.find((mode) =>
    mode.questions.find((q) => q._id === id),
  );
  const intro = question?._id
    ? resolveQuestionIntro(question, questionMode)
    : null;
  const showIntro = !!intro && !introDismissed && !showAnswer;
  // For a draw question the intro carries the QR, so the session opens here
  // and the question screen mirrors the same one. Without an intro the draw
  // board opens its own session as before.
  const introOwnsDraw = !!intro && !!question?.drawQrCodeId;
  const sharedHost = useDrawHost(introOwnsDraw ? question.drawQrCodeId : null);
  const drawHost = introOwnsDraw ? sharedHost : null;

  // Mirror the question / answer / who-answered view to live spectators.
  // The answer only ships once revealed. No-op when not broadcasting.
  useEffect(() => {
    if (!webGmBridge.isActive() || !game || !question?._id || showIntro) return;
    const modeName = game?.modes?.find((mode) =>
      mode.questions.find((q) => q._id === id),
    )?.name;
    webGmBridge.emitState(
      webGmBridge.buildQuestionState({
        game,
        question,
        modeName,
        showAnswer,
        showWhoAnswered,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, question?._id, showAnswer, showWhoAnswered, showIntro]);

  useEffect(() => {
    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  const introScreen = showIntro ? (
    <QuestionIntro
      intro={intro}
      title={questionMode?.name}
      theme={theme}
      drawHost={drawHost}
      onContinue={() => setIntroDismissed(true)}
    />
  ) : null;

  return (
    <SharedDrawHost.Provider value={drawHost}>
      <div className="h-full w-full flex flex-col font-sans select-none ">
        {theme === "MKH" ? (
          <>
            <MKHHeader theme={theme} game={game} setGame={setGame} ads={ads} />

            {introScreen ? (
              introScreen
            ) : showAnswer ? (
              <>
                {showWhoAnswered ? (
                  <MKHWhoAnswered
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                ) : (
                  <MKHAnswer
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                )}
              </>
            ) : (
              <MKHQuestion
                theme={theme}
                game={game}
                setGame={setGame}
                ads={ads}
                question={question}
                setShowAnswer={setShowAnswer}
              />
            )}
          </>
        ) : theme === "SJ" ? (
          <>
            <SJHeader theme={theme} game={game} setGame={setGame} ads={ads} />

            {introScreen ? (
              introScreen
            ) : showAnswer ? (
              <>
                {showWhoAnswered ? (
                  <SJWhoAnswered
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                ) : (
                  <SJAnswer
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                )}
              </>
            ) : (
              <SJQuestion
                theme={theme}
                game={game}
                setGame={setGame}
                ads={ads}
                question={question}
                setShowAnswer={setShowAnswer}
              />
            )}
          </>
        ) : (
          <>
            <TarbaaHeader
              theme={theme}
              game={game}
              setGame={setGame}
              ads={ads}
            />
            {introScreen ? (
              introScreen
            ) : showAnswer ? (
              <>
                {showWhoAnswered ? (
                  <WhoAnswered
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                ) : (
                  <TarbaaAnswer
                    theme={theme}
                    game={game}
                    setGame={setGame}
                    ads={ads}
                    question={question}
                    setShowAnswer={setShowAnswer}
                    setShowWhoAnswered={setShowWhoAnswered}
                  />
                )}
              </>
            ) : (
              <TarbaaQuestion
                theme={theme}
                game={game}
                setGame={setGame}
                ads={ads}
                question={question}
                setShowAnswer={setShowAnswer}
              />
            )}
          </>
        )}
      </div>
    </SharedDrawHost.Provider>
  );
};

export default Question;
